import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import ApiRequest from "../../helper/ApiRequest";
import { BaseUrl } from "../../helper/BaseUrl";
import CustomSelect from "../../component/CustomSelect";
import CustomImageUpload from "../../component/CustomImageUpload";
import DynamicCKEditor from "../../component/DynamicCkEditor";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";

const blogEditEndpoint = ApiEndpoints.adminBlogs.blogEdit;
const blogUpdateEndpoint = ApiEndpoints.adminBlogs.blogUpdate;

export default function EditBlog() {
  const navigate = useNavigate();
  const { blog_id } = useParams();

  const emptySection = () => ({
    _id: Date.now().toString() + Math.random(),
    heading: "",
    description: "",
  });

  const [sections, setSections] = useState([
    emptySection(),
    emptySection(),
  ]);

  const [previews, setPreviews] = useState({ thumbnail: null, banner_image: null, image1: null, });

  const [postData, setPostData] = useState({
    title: "", slug: "", category_id: "", author_id: "", tags: "", date: new Date(), brief: "", introduction: "", reference: "", thumbnail: null, alt_thumbnail: "", banner_image: null, alt_banner_image: "", image1: null, alt_image1: "",
  });

  const [data, setData] = useState({
    blog: {},
    blog_categories: [],
    authors: [],
  });


  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiRequest(
          "GET",
          `${blogEditEndpoint}${blog_id}`
        );

        setData(response);
        const blogData = response.blog || {};

        // ✅ fill postData
        setPostData({
          title: blogData.title || "",
          slug: blogData.slug || "",
          category_id: blogData.category_id || "",
          author_id: blogData.author_id || "",
          tags: blogData.tags || "",
          date: blogData.date || "",
          brief: blogData.brief || "",
          introduction: blogData.introduction || "",
          reference: blogData.reference || "",
          thumbnail: null,
          alt_thumbnail: blogData.alt_thumbnail || "",
          banner_image: null,
          alt_banner_image: blogData.alt_banner_image || "",
          image1: null,
          alt_image1: blogData.alt_image1 || "",
        });

        // ✅ load dynamic sections
        if (blogData.full_description?.length) {
          const formatted = blogData.full_description.map(sec => ({
            _id: sec._id || Date.now().toString() + Math.random(),
            heading: sec.heading || "",
            description: sec.description || "",
          }));

          setSections(formatted);
        } else {
          setSections([emptySection(), emptySection()]);
        }

        // ✅ previews
        setPreviews({
          thumbnail: blogData.thumbnail ? BaseUrl + blogData.thumbnail : null,
          banner_image: blogData.banner_image ? BaseUrl + blogData.banner_image : null,
          image1: blogData.image1 ? BaseUrl + blogData.image1 : null,
        });
      } catch (error) {
        console.error("Error fetching blog data:", error);
      }
    };

    fetchData();
  }, [blog_id]);


  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      const file = files[0];

      setPreviews((prev) => ({
        ...prev,
        [name]: URL.createObjectURL(file),
      }));

      setPostData((prev) => ({
        ...prev,
        [name]: file,
      }));
    } else {
      let updatedValue = value;
      if (name === "date") {
        updatedValue = new Date(value).toISOString();
      }

      setPostData((prev) => ({
        ...prev,
        [name]: updatedValue,
      }));
    }
  };


  const handleEditorChange = (key, value) => {
    setPostData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };


  // ================= add more heading =================
  const handleSectionChange = (id, field, value) => {
    setSections(prev =>
      prev.map(section =>
        section._id === id ? { ...section, [field]: value } : section
      )
    );
  };

  const addImageBlock = () => {
    setSections(prev => [...prev, emptySection()]);
  };

  const removeImageBlock = (id) => {
    setSections(prev => prev.filter(section => section._id !== id));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // ===== basic validation =====
    for (const [key, value] of Object.entries(postData)) {
      // Skip optional fields
      if (["reference"].includes(key)) continue;
      // Special handling for images in edit mode
      if (["thumbnail", "banner_image", "image1"].includes(key)) {
        if (!value && !previews[key]) {
          toast.error(`The field "${key}" is mandatory.`);
          return;
        }
        continue;
      }
      if (value === "" || value === null) {
        toast.error(`The field "${key}" is mandatory.`);
        return;
      }
    }

    // ===== section validation =====
    for (let i = 0; i < sections.length; i++) {
      const heading = sections[i].heading?.trim();
      const description = sections[i].description?.trim();

      if (!heading || !description) {
        toast.error(`Heading and Description ${i + 1} are mandatory.`);
        return;
      }
    }

    const formData = new FormData();
    Object.entries(postData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // ✅ send sections
    const cleanedSections = sections.map(sec => {
      const isMongoId = /^[a-f\d]{24}$/i.test(sec._id);

      if (isMongoId) {
        return sec;
      }

      const { _id, ...rest } = sec;
      return rest;
    });

    formData.append("full_description", JSON.stringify(cleanedSections));

    try {
      const response = await ApiRequest(
        "POST",
        blogUpdateEndpoint + blog_id,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast.success(response.message);
      navigate("/blog");
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };


  const pagename = {
    name: "Blog Management",
    user: "Admin",
    innerpage: "Blog Management / Blog / Edit Blog",
  };

  // ---------------------- RENDER ----------------------

  return (
    <>
      <AdminHeader pagename={pagename} />

      <div className="add-content-page">
        <form id="blogEdit" onSubmit={handleSubmit}>
          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>title<small>*</small></label>
                  </div>
                  <input onChange={handleChange} value={postData.title} name="title" type="text" className="form-control" placeholder="Enter Title" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>slug<small>*</small></label>
                  </div>
                  <input onChange={handleChange} value={postData.slug} name="slug" type="text" className="form-control" placeholder="Enter Slug" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>category<small>*</small></label>
                  </div>
                  <CustomSelect defaultValue={postData.category_id} onChange={handleChange} name="category_id" options={data.blog_categories} placeholder="Select Category" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Author Name<small>*</small></label>
                  </div>
                  <CustomSelect onChange={handleChange} defaultValue={postData.author_id} name="author_id" options={data.authors} placeholder="Select Author Name" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Tags (Comma Separated)<small>*</small></label>
                  </div>
                  <input onChange={handleChange} value={postData.tags} name="tags" type="text" className="form-control" placeholder="Enter Tags" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Date<small>*</small></label>
                  </div>
                  <input onChange={handleChange} value={postData.date
                    ? new Date(postData.date).toISOString().split("T")[0]
                    : ""} name="date" type="date" className="form-control" placeholder="Enter Date" />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Brief Description<small>*</small></label>
                  </div>
                  <input onChange={handleChange} value={postData.brief} name="brief" type="text" className="form-control" placeholder="Enter Brief" />
                </div>
              </div>

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Introduction<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.introduction} onChange={(event, editor) => handleEditorChange("introduction", editor?.getData())} />
                </div>
              </div>
            </div>
          </div>


          <div className="add-content-wrap">
            <div className="row">
              {sections.map((section, index) => (
                <div className="col-lg-6" key={section._id}>
                  <div className="add-page-inputs">
                    <div className="label-wrap-add">
                      <label>Heading {index + 1} {index < 2 && <small>*</small>}</label>
                    </div>
                    <input type="text" className="form-control" value={section.heading}
                      onChange={(e) => handleSectionChange(section._id, "heading", e.target.value)} />
                  </div>

                  <div className="add-page-inputs">
                    <div className="label-wrap-add">
                      <label>Description {index + 1} {index < 2 && <small>*</small>}</label>
                    </div>
                    <DynamicCKEditor data={section.description} onChange={(event, editor) => handleSectionChange(section._id, "description", editor?.getData())} />
                  </div>

                  {sections.length > 2 && (
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => removeImageBlock(section._id)}>Remove</button>
                  )}
                </div>
              ))}

              <div className="col-lg-12 add-page-inputs">
                <button type="button" className="addmore-images w-100" onClick={addImageBlock}>
                  Add More +
                </button>
              </div>

            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Reference<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.reference} onChange={(event, editor) => handleEditorChange("reference", editor?.getData())} />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-4">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Thumbnail Image (508 × 330)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="thumbnail"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    requiredWidth={508}
                    requiredHeight={330}
                    imagePreview={previews.thumbnail} />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Thumbnail Image Alt Tag<small>*</small></label>
                  </div>
                  <input onChange={handleChange} value={postData.alt_thumbnail} name="alt_thumbnail" type="text" className="form-control" placeholder="Alt Tag" />
                </div>
              </div>

              <div className="col-lg-4">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Banner Image (1608 × 680)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="banner_image"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    requiredWidth={1608}
                    requiredHeight={680}
                    imagePreview={previews.banner_image}
                  />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Banner Image Alt Tag<small>*</small></label>
                  </div>
                  <input onChange={handleChange} value={postData.alt_banner_image} name="alt_banner_image" type="text" className="form-control" placeholder="Alt Tag" />
                </div>
              </div>

              <div className="col-lg-4">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Image 1 (1080 × 560)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="image1"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    requiredWidth={1080}
                    requiredHeight={560}
                    imagePreview={previews.image1}
                  />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Image 1 Alt Tag<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="alt_image1" value={postData.alt_image1} type="text" className="form-control" placeholder="Alt Tag" />
                </div>
              </div>
            </div>
          </div>
        </form>

        <button type="submit" form="blogEdit" className="default-btn">
          publish <span></span>
        </button>
      </div>
    </>
  );
}
